import {createSelector} from 'reselect';
import {
  AqForecastResponse,
  StationRecords,
  WeatherForecast,
} from '../../api/types';
import {Generic} from '../../utils';
import {
  getHash,
  getSelectedForecastModelByLocationHash,
} from '../location/selectors';
import {CommonRootState} from '../types';
import {
  getId,
  getSelectedForecastModelByGenericStationId,
} from '../weather/selectors';

const EMPTY_HISTORY_DATA: NonNullable<
  CommonRootState['weatherData']['historyData']
> = {};
const EMPTY_FORECAST_DATA: NonNullable<
  CommonRootState['weatherData']['forecastData']
> = {};
const EMPTY_LOCATION_FORECAST_DATA: NonNullable<
  CommonRootState['weatherData']['locationForecastData']
> = {};
const EMPTY_STATION_WEATHER_WARNINGS: NonNullable<
  CommonRootState['weatherData']['stationWeatherWarnings']
> = {};
const EMPTY_LOCATION_WEATHER_WARNINGS: NonNullable<
  CommonRootState['weatherData']['locationWeatherWarnings']
> = {};
const EMPTY_WEATHER_WARNING = {
  warnings: [] as never[],
  computed_at: null as number | null,
};

const getWeatherRawHistoryData = (state: CommonRootState) => {
  return state.weatherData.historyData || EMPTY_HISTORY_DATA;
};

export const getWeatherHistoryData = createSelector(
  getWeatherRawHistoryData,
  (data) => new Generic.StationDataHandler(data),
);

export const EMPTY_FORECAST = {
  data: {
    daily: [],
    hourly: [],
  },
  metadata: {
    forecast_model: null,
    mesh_size: null,
    runs: [],
  },
  computed_at: null,
} as WeatherForecast;

export const getWeatherRawForecastData = (state: CommonRootState) => {
  return state.weatherData.forecastData || EMPTY_FORECAST_DATA;
};

export const getStationForecastData = createSelector(
  getWeatherRawForecastData,
  getSelectedForecastModelByGenericStationId,
  getId,
  (forecastDataDict, model, stationId) => {
    const key = model
      ? model.serverId.meshSize
        ? `${stationId}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
        : Object.keys(forecastDataDict).find((k) =>
            k.startsWith(`${stationId}-${model.serverId.forecastModel}-`),
          )
      : Object.keys(forecastDataDict).find((k) => k.startsWith(`${stationId}`));

    if (!key || !(key in forecastDataDict)) {
      return EMPTY_FORECAST;
    } else {
      return forecastDataDict[key];
    }
  },
);

export const getRawLocationForecastData = (state: CommonRootState) => {
  return state.weatherData.locationForecastData || EMPTY_LOCATION_FORECAST_DATA;
};

export const getLocationForecastData = createSelector(
  getRawLocationForecastData,
  getSelectedForecastModelByLocationHash,
  getHash,
  (forecastDataDict, model, locationHash) => {
    const key = model
      ? model.serverId.meshSize
        ? `${locationHash}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
        : Object.keys(forecastDataDict).find((k) =>
            k.startsWith(`${locationHash}-${model.serverId.forecastModel}-`),
          )
      : Object.keys(forecastDataDict).find((k) =>
          k.startsWith(`${locationHash}`),
        );

    if (!key || !(key in forecastDataDict)) {
      return EMPTY_FORECAST;
    } else {
      return forecastDataDict[key];
    }
  },
);

export const getRawStationWeatherWarnings = (state: CommonRootState) => {
  return (
    state.weatherData.stationWeatherWarnings || EMPTY_STATION_WEATHER_WARNINGS
  );
};

export const getStationWeatherWarnings = createSelector(
  getRawStationWeatherWarnings,
  getId,
  (warningDataDict, stationId) => {
    return warningDataDict[stationId] || EMPTY_WEATHER_WARNING;
  },
);

export const getRawLocationWeatherWarnings = (state: CommonRootState) => {
  return (
    state.weatherData.locationWeatherWarnings || EMPTY_LOCATION_WEATHER_WARNINGS
  );
};

export const getLocationWeatherWarnings = createSelector(
  getRawLocationWeatherWarnings,
  getHash,
  (warningDataDict, locationHash) => {
    return warningDataDict[locationHash] || EMPTY_WEATHER_WARNING;
  },
);

// AQ Forecast selectors

export const EMPTY_AQ_FORECAST: AqForecastResponse = {
  data: {
    hourly: [],
    daily: [],
  },
  metadata: {
    forecast_model: '',
  },
  computed_at: 0,
};

export const getRawStationAqForecastData = (state: CommonRootState) => {
  return state.weatherData.aqForecastData || {};
};

export const getStationAqForecastData = createSelector(
  getRawStationAqForecastData,
  getId,
  (aqDataDict, stationId) => aqDataDict[String(stationId)] || EMPTY_AQ_FORECAST,
);

// Station records selectors

export const EMPTY_STATION_RECORDS: StationRecords = {};

export const getRawStationRecordsData = (state: CommonRootState) => {
  return state.weatherData.stationRecordsData || {};
};

export const getStationRecordsData = createSelector(
  getRawStationRecordsData,
  getId,
  (recordsDict, stationId) =>
    recordsDict[String(stationId)] || EMPTY_STATION_RECORDS,
);

const getRawStationOnThisDayData = (state: CommonRootState) =>
  state.weatherData.stationOnThisDayData || {};

/** `key` is `Records.onThisDayKey(stationId, date)`. */
export const getStationOnThisDayData = createSelector(
  getRawStationOnThisDayData,
  (_state: CommonRootState, key: string) => key,
  (dataByKey, key) => dataByKey[key] || EMPTY_STATION_RECORDS,
);

export const getRawLocationAqForecastData = (state: CommonRootState) => {
  return state.weatherData.locationAqForecastData || {};
};

export const getLocationAqForecastData = createSelector(
  getRawLocationAqForecastData,
  getHash,
  (aqDataDict, locationHash) => aqDataDict[locationHash] || EMPTY_AQ_FORECAST,
);
